import {isMatch} from "lodash";
import isArray from "lodash/isArray";
import keys from "lodash/keys";
import pickBy from "lodash/pickBy";
import union from "lodash/union";

import {CardStack, CardStackColumns} from "@/components/CardStack/CardStack.tsx";
import {
  FilterCategory,
  FilterSets,
  FilterValueType,
  getFilterValue,
} from "@/components/filterDropdown/FilterDropdown.ts";
import {KPICard} from "@/components/KPICard/KPICard.tsx";
import {BlockFilterSets, SummaryBlock} from "@/components/summaryBar/SummaryBar.types.ts";

type SummaryBarProps<T> = {
  stats: T;
  filterSets?: FilterSets<FilterCategory>;
  summaryBlocks: SummaryBlock<T>[];
  columns?: CardStackColumns;
  onFilterSetChange?: (filterSet: FilterSets<FilterCategory>) => void;
  className?: string;
};

const subtractFilterSets = <FilterCategoryType extends FilterCategory>(
  filterSets: FilterSets<FilterCategory>,
  blockFilterset: BlockFilterSets
): FilterSets<FilterCategoryType> => {
  const result: FilterSets<FilterCategoryType> = {};

  union(keys(filterSets), keys(blockFilterset)).forEach((key) => {
    const typedKey = key as keyof FilterSets<FilterCategoryType>;
    const filterValue = getFilterValue<FilterCategoryType>(filterSets, key);

    if (!isArray(filterValue) && !!filterValue) {
      result[typedKey] = filterValue;
    } else {
      result[typedKey] = ((filterValue as string[]) || []).filter(
        (value) => !(blockFilterset[key] || []).includes(value)
      ) as FilterValueType<FilterCategoryType>;
    }
  });

  return pickBy(result, (value: FilterValueType<FilterCategoryType>) => {
    if (isArray(value)) {
      return !!value.length;
    } else return true;
  }) as FilterSets<FilterCategoryType>;
};

const mergeFilterSets = <FilterCategoryType extends FilterCategory>(
  filterSets: FilterSets<FilterCategoryType>,
  blockFilterset: BlockFilterSets
): FilterSets<FilterCategoryType> => {
  const result: FilterSets<FilterCategoryType> = {};

  union(keys(filterSets), keys(blockFilterset)).map((key) => {
    const typedKey = key as keyof FilterSets<FilterCategoryType>;
    const filterValue = getFilterValue<FilterCategoryType>(filterSets, key);

    if (isArray(filterValue) || !filterValue) {
      result[typedKey] = union(filterValue || [], blockFilterset[key] || []) as FilterValueType<FilterCategoryType>;
    } else {
      result[typedKey] = filterValue;
    }
  });
  return result;
};

export const SummaryBar = <StatType, FilterCategoryType extends FilterCategory>({
  stats,
  filterSets,
  summaryBlocks,
  columns,
  onFilterSetChange,
  className,
}: SummaryBarProps<StatType>) => {
  return (
    <CardStack columns={columns} className={className}>
      {summaryBlocks.map((summaryBlock, id) => {
        return (
          <KPICard
            role={summaryBlock.applyFilterset ? "button" : undefined}
            key={`summary-${id}`}
            title={summaryBlock.renderTitle ? summaryBlock.renderTitle(stats) : summaryBlock.title}
            icon={summaryBlock.icon}
            variant={
              summaryBlock.applyFilterset && filterSets && isMatch(filterSets, summaryBlock.applyFilterset)
                ? "selected"
                : undefined
            }
            onClick={() => {
              if (summaryBlock.applyFilterset && onFilterSetChange && filterSets) {
                if (isMatch(filterSets, summaryBlock.applyFilterset)) {
                  onFilterSetChange(subtractFilterSets<FilterCategoryType>(filterSets, summaryBlock.applyFilterset));
                } else {
                  onFilterSetChange(mergeFilterSets<FilterCategoryType>(filterSets, summaryBlock.applyFilterset));
                }
              }
            }}
          >
            {summaryBlock.renderStat(stats)}
          </KPICard>
        );
      })}
    </CardStack>
  );
};
